import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import PageHelmet from '@/components/Base/PageHelmet';
import TimestampComponent from '@/components/Base/Timestamp/Timestamp';
import ProductsFiltersForm from '@/components/Pages/Home/ProductsFiltersForm';
import {
  getPublisherMainData,
  useGetContentHeaderMenu,
  useGetProductsWithCompany,
  useSeoMeta,
} from '@/hooks';
import {
  convertTermMonthsToDays,
  getFormattedShortYearDate,
  getMinusHours,
  getRateDisplay,
} from '@/utils';

import {
  Description,
  MainInfo,
  Menu,
  MenuItemLink,
  Title,
  Wrapper,
} from './HomePageHeader.styles';

function HomePageHeader() {
  const { formatMessage, locale } = useIntl();
  const { metaTitle, metaDescription, metaPreviewText, pageTitle } =
    useSeoMeta();
  const { products } = useGetProductsWithCompany({
    locale,
  });

  const updatedDate = getFormattedShortYearDate(
    getMinusHours({ date: Date.now(), hours: 6 })
  );

  const contentBlocks = useGetContentHeaderMenu();
  const publisher = getPublisherMainData({
    description: formatMessage({ id: 'publisherDescription' }),
  });

  const mainSchema = useMemo(
    () => ({
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: formatMessage({ id: 'homePage.metaTitle' }),
        description: formatMessage({ id: 'homePage.metaDescription' }),
        url: 'https://minfin.com.ua/ua/credits/online/',
        inLanguage: 'uk-UA',
        isPartOf: {
          '@type': 'WebSite',
          url: 'https://minfin.com.ua/',
          publisher,
        },
      }),
    }),
    [formatMessage, pageTitle]
  );

  const productsSchema = useMemo(() => {
    if (!products) return null;

    return {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        mainEntity: {
          '@type': 'ItemList',
          name: pageTitle,
          itemListElement: products
            ?.slice(0, 5)
            ?.map(
              ({
                companyName,
                termMonths = {},
                loanRateYear,
                baseCalculatedRate,
                calculatedRateLimit,
                periodicalRateCollection,
                standardUird,
                loanLimitNumber,
                currency,
                logoUrl,
              }) => {
                const rateDisplay = getRateDisplay({
                  loanRateYear,
                  baseCalculatedRate,
                  calculatedRateLimit,
                  formatMessage,
                  periodicalRateCollection,
                  standardUird,
                });

                return {
                  '@type': 'LoanOrCredit',
                  name: formatMessage(
                    { id: 'shema.loanOrCredit.name' },
                    { companyName }
                  ),
                  provider: {
                    '@type': 'Organization',
                    name: companyName,
                    logo: {
                      '@type': 'ImageObject',
                      url: logoUrl,
                    },
                  },
                  loanTerm: {
                    '@type': 'QuantitativeValue',
                    ...convertTermMonthsToDays(termMonths),
                    unitText: 'days',
                  },
                  annualPercentageRate: {
                    '@type': 'QuantitativeValue',
                    name: formatMessage({ id: 'shema.loanDateYear.name' }),
                    value: rateDisplay?.rate || 0,
                  },
                  amount: {
                    '@type': 'MonetaryAmount',
                    name: formatMessage({ id: 'shema.amount.name' }),
                    minValue: loanLimitNumber?.min,
                    maxValue: loanLimitNumber?.max,
                    currency,
                  },
                };
              }
            ),
        },
      }),
    };
  }, [formatMessage, pageTitle, products]);

  const schema = useMemo(
    () => [mainSchema, ...(productsSchema ? [productsSchema] : [])],
    [mainSchema, productsSchema]
  );

  return (
    <Wrapper>
      <PageHelmet
        title={metaTitle}
        description={metaDescription}
        script={schema}
      />

      <MainInfo>
        <TimestampComponent latestTimestamp={updatedDate} />

        <Title>{pageTitle}</Title>

        {!!metaPreviewText && <Description>{metaPreviewText}</Description>}
      </MainInfo>

      <ProductsFiltersForm />

      <Menu>
        {contentBlocks.map(({ title, url }) => (
          <MenuItemLink key={url} to={url}>
            {title}
          </MenuItemLink>
        ))}
      </Menu>
    </Wrapper>
  );
}

export default HomePageHeader;
